import React from 'react';
import { keyframes, styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { useGetBillingPageUrl } from '@atlassian/jira-external-urls/src/utils/get-billing-page-url/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { CtaButton } from '@atlassian/jira-product-edition-upgrade/src/ui/cta-button/index.tsx';
import SparkleIcon from '@atlassian/jira-product-edition-upgrade/src/ui/sparkle-icon/index.tsx';
import type { ApplicationEdition } from '@atlassian/jira-shared-types/src/edition.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import messages from './messages';

type JswUpgradeButtonOldProps = {
	/**
	 * The name of the component or package using this button
	 */
	caller: string;
	/**
	 * The product edition to upgrade to. Useful if we want to link to the billing page with a specific edition pre-selected.
	 * If not provided, the button will link to the plan selection page.
	 */
	editionToUpgradeTo?: ApplicationEdition;
	/**
	 * Callback to be executed on click
	 */
	onClick?: (e: React.MouseEvent<HTMLElement>) => void;
};

/**
 * Button that takes the user to the Jira Software product billing page when clicked,
 * allowing them to upgrade their product edition.
 */
export const JswUpgradeButtonOld = ({
	caller,
	editionToUpgradeTo,
	onClick,
}: JswUpgradeButtonOldProps) => {
	const { productEntitlementDetails } = useTenantContext();
	const { formatMessage } = useIntl();

	const isUsingCcp = productEntitlementDetails?.['jira-software']?.billingSourceSystem === 'CCP';
	const {
		data: { billingPageUrl },
	} = useGetBillingPageUrl({ isUsingCcp, caller, editionToUpgradeTo });

	return (
		<ButtonWrapper>
			<CtaButton
				iconBefore={
					<IconWrapper>
						<SparkleIcon label="" />
					</IconWrapper>
				}
				onClick={onClick}
				href={billingPageUrl}
				target="_blank"
			>
				{formatMessage(messages.upgrade)}
			</CtaButton>
		</ButtonWrapper>
	);
};

type JswUpgradeButtonProps = {
	/**
	 * URL of the billing page allowing users to choose their plan
	 */
	billingPageUrl: string;
	/**
	 * Callback to be executed on click
	 */
	onClick?: (e: React.MouseEvent<HTMLElement>) => void;
	/**
	 *  Adds a data-testid attribute to the button
	 */
	testId?: string;
};

export const JswUpgradeButton = ({ billingPageUrl, onClick, testId }: JswUpgradeButtonProps) => {
	const { formatMessage } = useIntl();

	return (
		<ButtonWrapper>
			<CtaButton
				testId={testId}
				iconBefore={
					<IconWrapper>
						<SparkleIcon label="" />
					</IconWrapper>
				}
				onClick={onClick}
				href={billingPageUrl}
				target="_blank"
			>
				{formatMessage(messages.upgrade)}
			</CtaButton>
		</ButtonWrapper>
	);
};

const sparkleAnimation = keyframes({
	'0%': {
		transform: 'scale(0)',
		opacity: 0,
	},
	'40%': {
		transform: 'scale(1)',
		opacity: 1,
	},
	// The sparkle itself "ends" at 80%. We save the remaining 20% as a short delay before the animation starts again
	'80%': {
		transform: 'scale(0)',
		opacity: 0,
	},
	'100%': {
		transform: 'scale(0)',
		opacity: 0,
	},
});
const sparkleAnimationProperties = {
	animationName: sparkleAnimation,
	animationDuration: '2s',
	animationTimingFunction: 'ease-in-out',
	animationIterationCount: 'infinite',
	transformOrigin: 'center',
	transformBox: 'fill-box',
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonWrapper = styled.div({
	width: 'fit-content',

	// Animate sparkle icon on button hover
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& #starTopRight': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			...sparkleAnimationProperties,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& #starBottomRight': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			...sparkleAnimationProperties,
			// We delay this star's animation so the two stars sparkle separately
			animationDelay: '0.5s',
			// Because this star has an animation delay, we need to make it invisible to begin with
			// so it doesn't show up in its unanimated state for the first 0.5s of the animation (or however long we set the delay to).
			opacity: 0,
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.div({
	marginRight: token('space.075', '6px'),
});
